import { type ComponentProps, memo, type ReactNode } from 'react';

import { cn } from '@/shared/utils/cn';
import { getRarityFromProgress, rarityColors } from '@/shared/utils/rarity';

export interface ProgressCircleIndicatorProps extends ComponentProps<'div'> {
  progress: number;
  icon?: ReactNode;
  strokeWidth?: number;
  padding?: number;
  backgroundRingColor?: string;
  containerSize?: number;
  iconSize?: number;
}

function getColorForProgress(progress: number): string {
  const rarity = getRarityFromProgress(progress);

  if (!rarity) {
    return 'var(--color-warm-500)';
  }

  return rarityColors[rarity];
}

export const ProgressCircleIndicator = memo(
  ({
    progress,
    icon,
    className,
    strokeWidth = 5,
    padding = 6,
    backgroundRingColor = 'var(--color-cold-700)',
    containerSize = 64,
    iconSize = 28,
    ...props
  }: ProgressCircleIndicatorProps) => {
    const clampedProgress = Math.min(Math.max(progress, 0), 100);

    // Determine color based on progress
    const progressColor = getColorForProgress(clampedProgress);

    // Circle dimensions: container minus padding on both sides
    const circleDiameter = containerSize - padding * 2;
    const radius = (circleDiameter - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (clampedProgress / 100) * circumference;

    return (
      <div
        data-slot="progress-circle-indicator"
        className={cn(
          'relative flex items-center justify-center rounded-full bg-white/10 shadow-md backdrop-blur-sm',
          className,
        )}
        style={{
          width: containerSize,
          height: containerSize,
          padding: padding,
        }}
        {...props}
      >
        <svg
          data-slot="progress-circle-svg"
          className="absolute"
          width={circleDiameter}
          height={circleDiameter}
          viewBox={`0 0 ${circleDiameter.toString()} ${circleDiameter.toString()}`}
        >
          {/* Background ring (gray) */}
          <circle
            data-slot="background-ring"
            cx={circleDiameter / 2}
            cy={circleDiameter / 2}
            r={radius}
            fill="none"
            stroke={backgroundRingColor}
            strokeWidth={strokeWidth}
          />

          {/* Progress ring (colored) */}
          <circle
            data-slot="progress-ring"
            cx={circleDiameter / 2}
            cy={circleDiameter / 2}
            r={radius}
            fill="none"
            stroke={progressColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${(circleDiameter / 2).toString()} ${(circleDiameter / 2).toString()})`}
          />
        </svg>

        <div
          data-slot="icon-container"
          className="relative z-10 flex items-center justify-center"
          style={{
            width: iconSize,
            height: iconSize,
          }}
        >
          {icon}
        </div>
      </div>
    );
  },
);
